<template>
  <div class="maintenance-data">
    <label class="m-0">
      {{ $t('engineering.commutator-maintenance.sensor-data') }}
    </label>
    <b-row class="maintenance-data__sensor-data">
      <b-col cols="auto">
        <div>
          <label>
            {{ $t('engineering.commutator-maintenance.maintenance-identified-by-ied') }}:
          </label>
          <span>
            {{ getBooleanValue(iedData.maintenanceIdentifiedByIed) }}
          </span>
        </div>
        <div class="maintenance-data__ied-second-col">
          <label class="m-0">
            {{ $t('engineering.commutator-maintenance.previous-reading') }}:
          </label>
          <span>
              {{ iedData.previousReading || '-' }}
          </span>
        </div>
      </b-col>
      <b-col cols="auto">
        <div>
          <label>
            {{ $t('general.date-and-time') }}:
          </label>
          <span>
            {{ iedData.dateTime | dateTimeFilter({ format: formats.DATETIME_SHORT }) }}
          </span>
        </div>
        <div class="maintenance-data__ied-second-col">
          <label class="m-0">
            {{ $t('engineering.commutator-maintenance.further-reading') }}:
          </label>
          <span>
            {{ iedData.furtherReading || '-' }}
          </span>
        </div>
      </b-col>
    </b-row>
    <b-row class="mt-3">
      <b-col
        cols="auto"
        lg="6"
      >
        <label class="mb-1">
          {{ $t('engineering.commutator-maintenance.action') }}
        </label>
        <s-enum-select
          v-model="maintenanceActionClone.action"
          no-select-option
          no-sort
          translation-path="enums.engineering.commutator-maintenance"
          :enum-definition="actions"
          :disabled="disableActionDropdown"
          @input="updateMaintenanceAction"
        />
      </b-col>
      <b-col
        v-show="!isIgnoreOrImproperActionSelected"
        class="px-lg-1"
        cols="auto"
        lg="2"
      >
        <label class="mb-1">
          {{ $t('engineering.commutator-maintenance.duration') }}*
        </label>
        <b-input
          class="text-right"
          v-model="maintenanceActionClone.duration"
          v-mask="['##:##', '###:##', '####:##']"
          :state="$bsStateValidator($v.maintenanceActionClone.duration.$invalid)"
          :disabled="visualizationMode"
          @input="updateMaintenanceAction"
        />
      </b-col>
      <b-col
        v-show="!isIgnoreOrImproperActionSelected"
        class="maintenance-data__maintenance-date"
        :class="{ disabled: visualizationMode }"
        cols="auto"
        lg="4"
      >
        <label class="mb-1">
          {{ $t('engineering.commutator-maintenance.maintenance-date') }}*
        </label>
        <s-input-date-picker
          v-model="maintenanceActionClone.maintenanceDate"
          :disabled="visualizationMode"
          :min-date="minDate"
          :max-date="maxDate"
          :state="$bsStateValidator($v.maintenanceActionClone.maintenanceDate.$invalid)"
          @input="updateMaintenanceAction"
        />
      </b-col>
    </b-row>
    <b-row
      class="mt-3"
      :class="{ 'mt-5': isIgnoreActionSelected }"
    >
      <b-col>
        <b-form-textarea
          v-show="!isIgnoreActionSelected"
          v-model="maintenanceActionClone.observation"
          v-b-tooltip.hover.bottom="textareaPlaceholder"
          no-resize
          maxlength="2000"
          rows="2"
          :placeholder="textareaPlaceholder"
          :disabled="visualizationMode"
          @input="updateMaintenanceAction"
        />
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { requiredUnless } from 'vuelidate/lib/validators';
import { CommutatorMaintenanceActions } from 'sigma-mappings';
import {
  getInitialDate,
  getNow,
  isGreaterOrEqual,
  isLessOrEqual,
  setToStartOfDay,
} from '@/services/dateTime/datetimeService';
import { formats } from '@/constants/localizer';

export function normalizeValueDate(value) {
  return setToStartOfDay(value);
}

export function timeValidator(value = '') {
  if (!value || !this || this.isIgnoreOrImproperActionSelected) return true;
  const hour = Number(value.split(':')[0]) || 0;
  const minutes = Number(value.split(':').pop()) || 0;
  return hour !== 0
    ? minutes >= 0 && minutes <= 59
    : minutes >= 1 && minutes <= 59;
}

export function lengthValidator(value = '') {
  if (!value || !this || this.isIgnoreOrImproperActionSelected) return true;
  return [5, 6, 7].includes(value.length);
}

export function minDateValidator(value) {
  if (!value || !this || this.isIgnoreOrImproperActionSelected) return true;
  const dateWithoutTime = normalizeValueDate(value);
  return isLessOrEqual(this.minDate, dateWithoutTime);
}

export function maxDateValidator(value) {
  if (!value || !this || this.isIgnoreOrImproperActionSelected) return true;
  const dateWithoutTime = normalizeValueDate(value);
  return isGreaterOrEqual(this.maxDate, dateWithoutTime);
}

export function requiredUnlessValidator() {
  return this.isIgnoreOrImproperActionSelected;
}

export default {
  name: 'MaintenanceData',
  props: {
    maintenance: {
      type: Object,
      default: () => ({}),
    },
    maintenanceAction: {
      type: Object,
      default: () => ({}),
    },
    visualizationMode: {
      type: Boolean,
      default: false,
    },
  },
  data: vm => ({
    formats,
    maintenanceActionClone: vm.maintenanceAction,
  }),
  validations: {
    maintenanceActionClone: {
      duration: {
        hasTime: timeValidator,
        length: lengthValidator,
        requiredUnless: requiredUnless(requiredUnlessValidator),
      },
      maintenanceDate: {
        minValue: minDateValidator,
        maxValue: maxDateValidator,
        requiredUnless: requiredUnless(requiredUnlessValidator),
      },
    },
  },
  computed: {
    actions() {
      const identifiedByIdOptions = [
        CommutatorMaintenanceActions.IGNORE_AND_DONT_RESOLVES,
        CommutatorMaintenanceActions.CONFIRM_MAINTENANCE,
        CommutatorMaintenanceActions.IMPROPER_MAINTENANCE,
      ];
      const notIdentifiedByIdOptions = [
        CommutatorMaintenanceActions.CONFIRM_MAINTENANCE,
      ];
      return this.iedData.maintenanceIdentifiedByIed
        ? identifiedByIdOptions
        : notIdentifiedByIdOptions;
    },
    minDate() {
      return getInitialDate();
    },
    maxDate() {
      const maxDate = this.isMaintenanceIdentifiedByIed
        ? this.iedData.dateTime
        : getNow();
      return setToStartOfDay(maxDate);
    },
    disableActionDropdown() {
      return this.visualizationMode || !this.iedData.maintenanceIdentifiedByIed;
    },
    isIgnoreActionSelected() {
      return this.maintenanceActionClone.action
        === CommutatorMaintenanceActions.IGNORE_AND_DONT_RESOLVES;
    },
    isIgnoreOrImproperActionSelected() {
      return this.maintenanceActionClone.action
        === CommutatorMaintenanceActions.IGNORE_AND_DONT_RESOLVES
        || this.maintenanceActionClone.action
        === CommutatorMaintenanceActions.IMPROPER_MAINTENANCE;
    },
    textareaPlaceholder() {
      return `${this.$t('fields.general.observations')}...`;
    },
    iedData() {
      return this.maintenance.iedData || {};
    },
    isMaintenanceIdentifiedByIed() {
      return this.iedData.maintenanceIdentifiedByIed;
    },
  },
  methods: {
    updateMaintenanceAction() {
      const value = {
        ...this.maintenanceAction,
        ...this.maintenanceActionClone,
        $invalid: this.$v.$invalid,
      };
      this.$emit('update:maintenance-action', value);
    },
    getBooleanValue(data) {
      const values = {
        true: this.$t('general.yes'),
        false: this.$t('general.no'),
      };
      return values[data] || values.false;
    },
  },
  watch: {
    maintenanceAction() {
      this.maintenanceActionClone = this.maintenanceAction;
    },
  },
};
</script>

<style lang="sass" scoped>
@import "src/styles/defaults/variables"
@import src/styles/defaults/mixins

.maintenance-data
  max-width: 550px
  margin: 0 auto
  .form-control:disabled,
  ::v-deep .form-control:disabled
    opacity: 0.65 !important
  textarea.form-control:disabled
    @include s-placeholder-custom(var(--font-color-primary), 1, 'italic')
  &__maintenance-date.disabled ::v-deep
    & .input-group .form-control
      border-top-right-radius: 0.25rem
      border-bottom-right-radius: 0.25rem
    & .input-group-append
      display: none
  &__sensor-data
    border: 1px solid $color-border-gray
    padding: 5px
    margin: 0
    display: flex
    justify-content: space-between
    > div
      padding: 0 5px
  &__ied-second-col
    min-width: 200px
  ::v-deep
    .form-control.is-invalid
      padding-right: calc(1em + 0.75rem)
.manage-fonts_18px
  .maintenance-data
    max-width: 700px
.manage-fonts_16px
  .maintenance-data
    max-width: 600px
</style>
